import { Component, computed, inject, Signal } from '@angular/core';
import { TableComponent } from 'src/app/shared/table/components/table/table.component';
import {
  ProfilelistActions,
  ProfilelistCloneActions,
  selectProfilelistGridData,
} from '../../profilelist.feature';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { ColDef } from 'ag-grid-enterprise';
import { Profile } from 'src/app/shared/entities/profile';
import {
  ColumnFilter,
  DataRequest,
  GridSort,
} from 'src/app/shared/table/components/table/table.types';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { SpinnerComponent } from '../../../../shared/components/spinner/spinner.component';
// import { IconMenuRendererComponent } from 'src/app/shared/table/components/cell-renderers/icon-menu-renderer/icon-menu-renderer.component';
// import { IconMenuConfig } from 'src/app/shared/table/components/cell-renderers/icon-menu-renderer/icon-menu-renderer.types';
import { DialogService } from 'src/app/shared/dialog/services/dialog/dialog.service';
import { externalTextColumnFilter } from 'src/app/shared/table/components/filters/external-text-column-filter';
import { IconMenuConfig } from 'src/app/shared/table/components/cell-renderers/icon-menu-renderer/icon-menu-renderer.types';
import { IconMenuRendererComponent } from 'src/app/shared/table/components/cell-renderers/icon-menu-renderer/icon-menu-renderer.component';
import { PageService } from 'src/app/shared/page/service/page.service';
import { Page } from 'src/app/shared/page/page.config';
import { MatButtonModule } from '@angular/material/button';
import { ProfileDataService } from 'src/app/shared/store/features/profile-data/profile-data.service';
import { map } from 'rxjs';
import { TotalComponent } from '../../../../shared/components/total/total.component';
import { Lang } from 'src/app/shared/services/lang/lang.types';
import { ProfileType } from 'src/app/shared/entities/profile-type';
import { UserSelector } from 'src/app/shared/store/user/user.feature';

@Component({
  selector: 'app-profilelist-main-table',
  imports: [TableComponent, SpinnerComponent, MatButtonModule, TotalComponent],
  templateUrl: './main-table.component.html',
  styleUrl: './main-table.component.scss',
})
export class MainTableComponent {
  private langService = inject(LangService);
  private lang = this.langService.lang;
  private dialogService = inject(DialogService);
  private pageService = inject(PageService);
  private profileDataService = inject(ProfileDataService);
  labels = this.langService.lang.features.profilelist;
  private store = inject(Store);
  private gridData$ = this.store.select(selectProfilelistGridData);
  gridData = toSignal(this.gridData$, {
    initialValue: {
      filter: [],
      sort: GridSort.none,
      rowData: [],
      loading: false,
      paginationState: {
        currentPage: 0,
        totalRecords: 0,
        recordsPerPage: 0,
      },
    },
  });
  total = toSignal(
    this.profileDataService.data$.pipe(map(data => data?.length ?? 0)),
  );
  isEditor = toSignal(this.store.select(UserSelector.selectIsEditor));

  isDeleting = toSignal(this.profileDataService.isDeleting);
  spinning = computed(() => this.gridData().loading || this.isDeleting());

  private actionButtons: IconMenuConfig<Profile> = {
    buttons: [
      {
        icon: 'edit',
        tooltip: this.labels.actions.edit.tooltip,
        callback: data => {
          if (data.profileName) {
            this.store.dispatch(
              ProfilelistActions.edit({ key: data.profileName }),
            );
          }
        },
      },
      {
        icon: 'file_copy',
        tooltip: this.labels.actions.clone.tooltip,
        callback: data => {
          if (data.profileName) {
            this.store.dispatch(
              ProfilelistCloneActions.edit({ key: data.profileName }),
            );
          }
        },
      },
      {
        icon: 'delete',
        tooltip: this.labels.actions.delete.tooltip,
        callback: data => {
          this.dialogService.withConfirmation({
            text: this.labels.actions.delete.confirmation(
              data.profileName ?? '',
            ),
          })(() => this.profileDataService.delete(data));
        },
      },
    ],
  };

  private profileTypeLabel(profileType: string, lang: Lang): string {
    // In the regular case, return the standard label associated with the given
    // profile type.  If the type is empty, return "Unknown".  If the type is
    // not empty but not standard, return it.  The last two options should not
    // occur in practice, but they depend on data sent by the BE.
    if (!profileType) {
      return lang.shared.profileTypeUnknown;
    }
    const standardProfileType = Object.values(ProfileType).find(
      pt => pt === profileType,
    );
    if (standardProfileType) {
      const profileTypeLabels = lang.shared.profileType;
      return profileTypeLabels[standardProfileType];
    }
    return profileType;
  }

  private dataColumnDefs: ColDef<Profile>[] = [
    {
      headerName: this.labels.headers.profileName,
      field: 'profileName',
      cellClass: ['link-color', 'curPointer'],
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: [externalTextColumnFilter],
      },
      onCellClicked: event => {
        const key = event.data?.profileName;
        if (key) {
          this.store.dispatch(ProfilelistActions.drilldown({ key }));
        }
      },
    },
    {
      headerName: this.labels.headers.profileType,
      colId: 'profileType',
      valueGetter: p =>
        this.profileTypeLabel(p.data?.profileType ?? '', this.lang),
      minWidth: 50,
      maxWidth: 90,
    },
  ];

  private actionColumnDef = {
    sortable: false,
    minWidth: 60,
    maxWidth: 160,
    cellRenderer: IconMenuRendererComponent,
    valueGetter: () => this.actionButtons,
  };

  private editorColumnDefs = [...this.dataColumnDefs, this.actionColumnDef];

  columnDefs: Signal<ColDef<Profile>[]> = computed(() => {
    const isEditor = this.isEditor();
    return isEditor ? this.editorColumnDefs : this.dataColumnDefs;
  });

  onDataRequest(event: DataRequest) {
    this.store.dispatch(ProfilelistActions.dataRequest(event));
  }

  onFilterChanged(event: ColumnFilter) {
    this.store.dispatch(ProfilelistActions.applyFilter({ filter: event }));
  }

  onSortChanged(event: GridSort) {
    this.store.dispatch(ProfilelistActions.applySorting({ sort: event }));
  }

  onCreate() {
    this.pageService.goToPage(Page.CreateProfile);
  }
}
